import { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';
import { PAGINATION_DEFAULT_OPTIONS } from '@routes/sidebar/admin-center/___shared___/components/tables/constants.ts';
import {
    defaultRowClassName,
    FilterEmptyText,
} from '@routes/sidebar/admin-center/___shared___/components/tables/helpers.tsx';
import type {
    CommonRegularTableProps,
    CommonTableRow,
} from '@routes/sidebar/admin-center/___shared___/components/tables/types.ts';
import { TableEmptyOrFilteredText } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/components/table-empty-state';
import { hidePagination } from '@utils/pagination.ts';
import type { TableProps } from 'antd';
import type { TableLocale } from 'antd/es/table/interface';

import { StyledTable } from './styled.ts';

export const CommonRegularTable = <T extends CommonTableRow>({
    rows,
    columns,
    pagination,
    scrollY,
    onPaginationChange,
    onTableChange,
    emptyTitle,
    emptyDescription,
    emptyTitleWhenFiltered,
    emptyDescWhenFiltered,
    expandable,
    rowClassName,
    rowSelection,
    loading = false,
    hasActiveFilters = false,
    tableLayout = 'auto',
    dataCy,
}: CommonRegularTableProps<T>) => {
    const { t } = useLingui();

    const handleChange: TableProps<T>['onChange'] = (pag, filters, sorter, extra) => {
        if (onPaginationChange && pag) {
            const cur = (pag as any)?.current ?? (pagination && pagination.pageNumber);
            const size = (pag as any)?.pageSize ?? (pagination && pagination.pageSize);

            if (pagination && (cur !== pagination.pageNumber || size !== pagination.pageSize)) {
                onPaginationChange(cur ?? 1, size ?? 10);
            }
        }

        onTableChange?.(pag, filters, sorter, extra);
    };

    const customPagination = useMemo<TableProps<T>['pagination']>(() => {
        //show pagination only if total items is greater than 10
        if (!pagination || hidePagination(pagination.totalItems)) return false;

        return {
            ...PAGINATION_DEFAULT_OPTIONS,
            current: pagination.pageNumber,
            pageSize: pagination.pageSize,
            total: pagination.totalItems,
        };
    }, [pagination]);

    const emptyText = (
        <TableEmptyOrFilteredText
            hasActiveFilters={hasActiveFilters}
            emptyTitle={emptyTitle}
            emptyDescription={emptyDescription}
            emptyTitleWhenFiltered={emptyTitleWhenFiltered}
            emptyDescWhenFiltered={emptyDescWhenFiltered}
        />
    );

    const filterEmptyText = <FilterEmptyText title={t`No filters`} />;

    const rowsCount = rows?.length ?? 0;
    const hasRows = rowsCount > 0;

    const noData = !loading && !hasRows;
    const noRecordsFound = !loading && hasActiveFilters && !hasRows;

    const tableLocale: TableLocale = {
        emptyText,
        filterSearchPlaceholder: t`Search`,
        filterConfirm: t`OK`,
        filterReset: t`Reset`,
        filterEmptyText,
    };

    return (
        <StyledTable
            tableLayout={tableLayout}
            rowHoverable={false}
            $noData={noData}
            $noRecordsFound={noRecordsFound}
            columns={columns}
            getPopupContainer={(triggerNode) =>
                (triggerNode?.closest?.('.ant-table-wrapper') as HTMLElement) ?? document.body
            }
            dataSource={rows}
            loading={loading}
            bordered={hasRows}
            scroll={{ x: 'max-content', y: scrollY }}
            locale={tableLocale}
            onChange={handleChange}
            $pageSizerLabel={t`Results per page`}
            pagination={customPagination}
            rowClassName={rowClassName ?? defaultRowClassName}
            expandable={expandable}
            rowSelection={rowSelection}
            data-cy={dataCy}
        />
    );
};
